import { ActionMenuItem } from '@thoughtspot/radiant-react/widgets/action-menu';
import { ButtonProps } from '@thoughtspot/radiant-react/widgets/button';
import { Action, Action as EmbedAction } from '/@utils/embed.util';

type AnswerActionToEmbedActionMapType = {
    [key in AnswerAction]?: EmbedAction;
};

export type ActionDisabilityConMap = {
    [key in Action]: () => boolean;
};

export type ActionDisabilityTooltipMap = {
    [key in Action]: () => string;
};

export type ActionItemVisibilityMap = {
    [key in AnswerAction]: () => boolean;
};

interface CascadingMenuItemConfig {
    /* Parent Action associated with a cascading menu item */
    parentAction: string;

    /* Label to be used when this is the only item in the cascading menu,
       the item will be moved to the parent menu and will not be a part of the cascading menu */
    labelIfSingleItem: string;
}

export enum AnswerAction {
    ADD_SCENARIO,
    EDIT, // pinboard viz edit
    EXPLORE,
    PIN,
    SPOTIQ_ANALYZE,
    UPDATE, // update pinboard viz
    SAVE, // save answer
    SAVE_VIEW,
    SAVE_AS_VIEW,
    EDIT_A_COPY,
    MONITOR,
    CREATE_MONITOR,
    MANAGE_MONITOR,
    ENABLE_CONTEXTUAL_CHANGE_ANALYSIS,
    ENABLE_ITERATIVE_CHANGE_ANALYSIS,
    SHOW_UNDERLYING_DATA,
    DOWNLOAD,
    DOWNLOAD_AS_PNG,
    DOWNLOAD_AS_CSV,
    DOWNLOAD_AS_PDF,
    DOWNLOAD_AS_XLSX,
    TML,
    EXPORT_TML,
    UPDATE_TML,
    EDIT_TML,
    DELETE,
    COPY_LINK,
    TILE_SIZE_SELECT,
    EDIT_TITLE_DESCRIPTION,
    RENAME_MODAL_TITLE_DESCRIPTION,
    PRESENT,
    MOVE_TO_TAB,
    REQUEST_ACCESS,
    SYNC,
    SYNC_TO_SHEETS,
    SYNC_TO_OTHER_APPS,
    MANAGE_PIPELINES,
    SHARE,
    INSERT_INTO_SLIDE,
    ASK_AI,
    SHOW_VERSION_HISTORY,
    ENABLE_VERSION_CONTROL,
    DISABLE_VERSION_CONTROL,
    SWITCH_TO_NEW_CHART_EXPERIENCE,
    SWITCH_TO_CLASSIC_CHART_EXPERIENCE,
    ADD_TO_WATCHLIST,
    REMOVE_FROM_WATCHLIST,
    PUBLISH,
    MANAGE_PUBLISHING,
    UNPUBLISH,
    REPORT_ISSUE,
    EDIT_INPUT_TABLE,
    IN_CONVERSATION_TRAINING,
}

export interface AnswerMenuActionItem extends ActionMenuItem {
    configIfCascading?: CascadingMenuItemConfig;
}

// This will be the action item config for the 3 dot action menu button
export interface AnswerMenuActionItemConfig {
    getConfig: () => AnswerMenuActionItem;
}

// Answer action id -> embed action id
export const AnswerActionToEmbedActionMap: AnswerActionToEmbedActionMapType = {
    [AnswerAction.ADD_SCENARIO]: EmbedAction.AddScenario,
    [AnswerAction.EDIT]: EmbedAction.EditVisualization,
    [AnswerAction.EXPLORE]: EmbedAction.Explore,
    [AnswerAction.PIN]: EmbedAction.Pin,
    [AnswerAction.SPOTIQ_ANALYZE]: EmbedAction.SpotIQAnalyze,
    [AnswerAction.UPDATE]: EmbedAction.Save, // update pinboard viz
    [AnswerAction.SAVE]: EmbedAction.Save, // save answer
    [AnswerAction.SAVE_VIEW]: EmbedAction.SaveAsView,
    [AnswerAction.SAVE_AS_VIEW]: EmbedAction.SaveAsView,
    [AnswerAction.EDIT_A_COPY]: EmbedAction.MakeACopy,
    [AnswerAction.IN_CONVERSATION_TRAINING]:
        EmbedAction.InConversationTraining,
    [AnswerAction.MONITOR]: EmbedAction.Monitor,
    [AnswerAction.CREATE_MONITOR]: EmbedAction.CreateMonitor,
    [AnswerAction.MANAGE_MONITOR]: EmbedAction.ManageMonitor,
    [AnswerAction.ENABLE_CONTEXTUAL_CHANGE_ANALYSIS]:
        EmbedAction.EnableContextualChangeAnalysis,
    [AnswerAction.ENABLE_ITERATIVE_CHANGE_ANALYSIS]:
        EmbedAction.EnableIterativeChangeAnalysis,
    [AnswerAction.SHOW_UNDERLYING_DATA]: EmbedAction.ShowUnderlyingData,
    [AnswerAction.DOWNLOAD]: EmbedAction.Download,
    [AnswerAction.DOWNLOAD_AS_PNG]: EmbedAction.DownloadAsPng,
    [AnswerAction.DOWNLOAD_AS_CSV]: EmbedAction.DownloadAsCsv,
    [AnswerAction.DOWNLOAD_AS_PDF]: EmbedAction.DownloadAsPdf,
    [AnswerAction.DOWNLOAD_AS_XLSX]: EmbedAction.DownloadAsXlsx,
    [AnswerAction.TML]: EmbedAction.TML,
    [AnswerAction.EXPORT_TML]: EmbedAction.ExportTML,
    [AnswerAction.UPDATE_TML]: EmbedAction.UpdateTML,
    [AnswerAction.EDIT_TML]: EmbedAction.EditTML,
    [AnswerAction.DELETE]: EmbedAction.AnswerDelete,
    [AnswerAction.COPY_LINK]: EmbedAction.CopyLink,
    [AnswerAction.TILE_SIZE_SELECT]: EmbedAction.ToggleSize,
    [AnswerAction.EDIT_TITLE_DESCRIPTION]: EmbedAction.EditTitleDescription,
    [AnswerAction.RENAME_MODAL_TITLE_DESCRIPTION]:
        EmbedAction.RenameModalTitleDescription,
    [AnswerAction.PRESENT]: EmbedAction.Present,
    [AnswerAction.MOVE_TO_TAB]: EmbedAction.MoveToTab,
    [AnswerAction.REQUEST_ACCESS]: EmbedAction.RequestAccess,
    [AnswerAction.SYNC]: EmbedAction.Sync,
    [AnswerAction.SYNC_TO_SHEETS]: EmbedAction.SyncToSheets,
    [AnswerAction.SYNC_TO_OTHER_APPS]: EmbedAction.SyncToOtherApps,
    [AnswerAction.MANAGE_PIPELINES]: EmbedAction.ManagePipelines,
    [AnswerAction.SHARE]: EmbedAction.Share,
    [AnswerAction.INSERT_INTO_SLIDE]: EmbedAction.InsertInToSlide,
    [AnswerAction.ASK_AI]: EmbedAction.AskAi,
    [AnswerAction.SHOW_VERSION_HISTORY]: EmbedAction.ShowVersionHistory,
    [AnswerAction.ENABLE_VERSION_CONTROL]: EmbedAction.EnableVersionControl,
    [AnswerAction.DISABLE_VERSION_CONTROL]: EmbedAction.DisableVersionControl,
    [AnswerAction.SWITCH_TO_NEW_CHART_EXPERIENCE]:
        EmbedAction.SwitchToNewChartExperience,
    [AnswerAction.SWITCH_TO_CLASSIC_CHART_EXPERIENCE]:
        EmbedAction.SwitchToClassicChartExperience,
    [AnswerAction.ADD_TO_WATCHLIST]: EmbedAction.AddToWatchlist,
    [AnswerAction.REMOVE_FROM_WATCHLIST]: EmbedAction.RemoveFromWatchlist,
    [AnswerAction.PUBLISH]: EmbedAction.Publish,
    [AnswerAction.MANAGE_PUBLISHING]: EmbedAction.ManagePublishing,
    [AnswerAction.UNPUBLISH]: EmbedAction.Unpublish,
    [AnswerAction.EDIT_INPUT_TABLE]: EmbedAction.EditInputTable,
};

export const AnswerEmbedActionsRequiringSave: AnswerAction[] = [
    AnswerAction.DELETE,
];

export interface AnswerMenuActionItemGroupConfig {
    title: string;
    items: EmbedAction[];
}

export interface AnswerButtonActionItemConfig {
    getButtonProps: (showButtonWithoutText?: boolean) => ButtonProps;
    dataTestId?: string;
    dataTourId?: string;
}
